import { useCallback, useRef, useSyncExternalStore } from 'react';
import {
  CanvasHistory,
  HISTORY_CAP,
  type HistoryEntry,
  type HistoryEntryWithState,
  type HistorySnapshot,
} from '../lib/canvas-history';
import { INSIDE_STROKE_FLAG } from '../lib/text-styling';

interface CanvasLike {
  /** Fabric v6 `toJSON()` is typed without args; `toObject(props)` is the typed
   *  alternative and produces the same JSON-serializable shape. */
  toJSON(): object;
  toObject(propertiesToInclude?: string[]): object;
  loadFromJSON(json: object): Promise<void>;
  renderAll(): void;
}

/** Custom Fabric object props that must round-trip through toJSON /
 *  loadFromJSON. Exported so the IndexedDB session-save flow in EditorPage
 *  can use the same list (single source of truth). */
export const SERIALIZED_PROPS = [
  'layerId',
  'id',
  '_isBackground',
  'archValue',
  'globalCompositeOperation',
  // Tier 2 Group B — inside-stroke render flag. The override
  // (`ensureInsideStrokeRenderOverride` in `lib/text-styling`)
  // reads this on every render; the override itself is
  // reinstalled per-instance by the post-load walk in
  // `applyLayerOrder`, but the FLAG has to survive the
  // `toObject` → `loadFromJSON` round-trip on its own.
  INSIDE_STROKE_FLAG,
];

interface UseCanvasHistoryOptions<TExtra = unknown> {
  /** Cap on non-manual entries. Manual snapshots are always kept.
   *  Default: HISTORY_CAP (200). */
  maxHistory?: number;
  /**
   * Optional invariant check fired right before each snapshot is recorded.
   * The hook still records the snapshot regardless — the validator's job
   * is purely diagnostic, surfacing recurring desync bugs (e.g. "canvas
   * has the Background image but the React layers list is empty, so the
   * next undo would wipe it") at the moment they're created instead of
   * minutes later when the user hits undo. Return a string describing
   * the problem, or `null` if everything looks consistent.
   */
  validateSnapshot?: (snapshot: { canvasJSON: object; extra?: TExtra }) => string | null;
}

/**
 * Optional extra-state hooks for snapshotting React state alongside the canvas
 * JSON. Used to capture/restore the layers list as part of undo/redo.
 */
export interface ExtraStateAccess<TExtra> {
  get: () => TExtra;
  set: (value: TExtra) => void;
}

/** Optional explicit-label override for `saveSnapshot`. When
 *  omitted, the label is inferred from the pending-label channel
 *  (set by EditorPage's dispatch wrapper) and falls back to "Edit".
 *  Manual snapshots use `manualSnapshot` instead. */
export interface SaveSnapshotOptions {
  label?: string;
  commandId?: string;
}

/**
 * Canvas-specific undo/redo built on top of the CanvasHistory engine.
 * Each snapshot stores the Fabric.js canvas JSON plus any optional extra
 * React state registered via `setExtraStateAccess`.
 *
 * Tier 3 Group C Phase 3: the engine raised the default cap from 50 →
 * 200, added per-entry labels + command-id tagging, and exposes
 * manualSnapshot / jumpTo / getEntries / subscribe for the History
 * panel. The pre-Phase-3 public surface (saveSnapshot / undoCanvas /
 * redoCanvas / clearHistory / getHistory / restoreHistory + canUndo /
 * canRedo / undoCount / redoCount / isRestoring) is preserved.
 */
export function useCanvasHistory<TExtra = unknown>({
  maxHistory = HISTORY_CAP,
  validateSnapshot,
}: UseCanvasHistoryOptions<TExtra> = {}) {
  const canvasRef = useRef<CanvasLike | null>(null);
  const isRestoringRef = useRef(false);
  const extraAccessRef = useRef<ExtraStateAccess<TExtra> | null>(null);
  // Validator lives in a ref so callers can pass a closure over fresh
  // state without re-binding the hook's identity on every render.
  const validateSnapshotRef = useRef(validateSnapshot);
  validateSnapshotRef.current = validateSnapshot;

  // Engine instance lives in a ref so each mount keeps the same
  // instance across renders. Lazy init mirrors the pattern used by
  // commandRegistryRef in EditorPage.
  const engineRef = useRef<CanvasHistory<TExtra> | null>(null);
  if (engineRef.current === null) {
    engineRef.current = new CanvasHistory<TExtra>(maxHistory);
  }
  const engine = engineRef.current;

  // Subscribe React to the engine's mutation stream. Returning the
  // version stamp is enough — `useSyncExternalStore` re-renders
  // every time the snapshot changes, and every method below reads
  // its display state (canUndo / undoCount / etc.) fresh from the
  // engine on render.
  useSyncExternalStore(
    useCallback((cb) => engine.subscribe(cb), [engine]),
    useCallback(() => engine.getVersion(), [engine]),
    () => 0,
  );

  /** Pending-label channel — populated by EditorPage's dispatch
   *  wrapper before invoking a command, cleared after. saveSnapshot
   *  with no explicit label picks up the in-flight command's label
   *  so the History panel reads "Brush" instead of "Edit". */
  const pendingLabelRef = useRef<{ label: string; commandId?: string } | null>(null);
  /** Always-on fallback label resolver. Most saveSnapshot calls
   *  come from canvas event handlers (paint, marquee, erase) that
   *  don't flow through the dispatch wrapper, so the
   *  pendingLabelRef is null. The resolver — registered once by
   *  EditorPage — looks at the active tool to produce a useful
   *  label like "Brush" or "Marquee select" instead of the
   *  generic "Edit" fallback. */
  const defaultLabelResolverRef = useRef<(() => string | null) | null>(null);

  const setCanvas = useCallback((canvas: CanvasLike | null) => {
    canvasRef.current = canvas;
  }, []);

  /**
   * Register getter/setter callbacks for additional React state to be
   * captured/restored alongside the canvas JSON. Stored in a ref so the
   * registration itself doesn't re-bind on every parent render.
   */
  const setExtraStateAccess = useCallback((access: ExtraStateAccess<TExtra> | null) => {
    extraAccessRef.current = access;
  }, []);

  /** Set or clear the pending-label channel. EditorPage's dispatch
   *  wrapper calls `setPendingLabel({ label, commandId })` before
   *  running a command's `run` body and `setPendingLabel(null)`
   *  after. */
  const setPendingLabel = useCallback((next: { label: string; commandId?: string } | null) => {
    pendingLabelRef.current = next;
  }, []);

  /** Register (or clear) the always-on fallback label resolver.
   *  EditorPage wires this once at mount with a closure that reads
   *  the live activeTool from a ref + maps it through
   *  `TOOL_HISTORY_LABELS` for past-tense action names. Pass
   *  `null` to disable the fallback (label reverts to "Edit"). */
  const setDefaultLabelResolver = useCallback((resolver: (() => string | null) | null) => {
    defaultLabelResolverRef.current = resolver;
  }, []);

  const runValidator = useCallback((canvasJSON: object, extra: TExtra | undefined) => {
    const validator = validateSnapshotRef.current;
    if (!validator) return;
    try {
      const problem = validator({ canvasJSON, extra });
      if (problem) {
        console.error(`[useCanvasHistory] snapshot invariant violation: ${problem}`, {
          canvasObjectCount: Array.isArray((canvasJSON as { objects?: unknown[] }).objects)
            ? (canvasJSON as { objects: unknown[] }).objects.length
            : 0,
          extra,
        });
      }
    } catch (err) {
      // A throwing validator is itself a bug worth surfacing, but we
      // never let it derail the snapshot — that's the user's escape
      // hatch from any subsequent state corruption.
      console.error('[useCanvasHistory] validateSnapshot threw', err);
    }
  }, []);

  /** Capture current canvas + extra state as a history snapshot.
   *  Backwards-compatible signature: callers that don't pass options
   *  get the pending-label fallback ("Edit" if nothing is in flight). */
  const saveSnapshot = useCallback(
    (opts: SaveSnapshotOptions = {}) => {
      const canvas = canvasRef.current;
      if (!canvas || isRestoringRef.current) return;
      const canvasJSON = canvas.toObject(SERIALIZED_PROPS);
      const extra = extraAccessRef.current?.get();
      runValidator(canvasJSON, extra);
      const label =
        opts.label ??
        pendingLabelRef.current?.label ??
        defaultLabelResolverRef.current?.() ??
        'Edit';
      const commandId = opts.commandId ?? pendingLabelRef.current?.commandId;
      engine.push({ canvasJSON, extra }, { label, commandId });
    },
    [engine, runValidator],
  );

  /** Capture the current canvas + extra state as a PINNED manual
   *  snapshot. Manual snapshots are cap-exempt — they survive the
   *  trim policy that drops the oldest non-manual entries when
   *  the 200-step cap is reached. */
  const manualSnapshot = useCallback(
    (label: string) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const canvasJSON = canvas.toObject(SERIALIZED_PROPS);
      const extra = extraAccessRef.current?.get();
      engine.push({ canvasJSON, extra }, { label, isManual: true });
    },
    [engine],
  );

  /** Apply a single snapshot entry to the canvas + extra state.
   *  loadFromJSON failures are caught + logged rather than thrown:
   *  a corrupt entry (storage tampering, Fabric.js version
   *  regression, partial-write IDB record) must not crash the
   *  editor. The canvas is left in whatever partial state
   *  loadFromJSON reached; subsequent user edits will save a
   *  fresh snapshot that overwrites the bad one. */
  const applyEntry = useCallback(async (snapshot: HistorySnapshot<TExtra>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    isRestoringRef.current = true;
    try {
      await canvas.loadFromJSON(snapshot.canvasJSON);
      canvas.renderAll();
      if (snapshot.extra !== undefined && extraAccessRef.current) {
        extraAccessRef.current.set(snapshot.extra);
      }
    } catch (err) {
      console.error('[useCanvasHistory] loadFromJSON failed during history apply', err);
    } finally {
      isRestoringRef.current = false;
    }
  }, []);

  const undoCanvas = useCallback(async () => {
    const prev = engine.undo();
    if (prev) await applyEntry(prev);
  }, [engine, applyEntry]);

  const redoCanvas = useCallback(async () => {
    const next = engine.redo();
    if (next) await applyEntry(next);
  }, [engine, applyEntry]);

  /** Apply a specific entry by id — click-to-jump in the History
   *  panel. No-ops silently for unknown ids or when the id matches
   *  the current entry. */
  const jumpTo = useCallback(
    async (entryId: string) => {
      const target = engine.jumpTo(entryId);
      if (target) await applyEntry(target);
    },
    [engine, applyEntry],
  );

  /**
   * Restore the entire history stack from a persisted source (e.g. IndexedDB)
   * AND apply the snapshot at the given index to the canvas + extra state.
   *
   * Accepts both the rich entry shape (Tier 3 Group C Phase 3+) and the
   * legacy `{ canvasJSON, extra }[]` shape (pre-Phase 3 IDB sessions).
   * Engine handles the normalization.
   */
  const restoreHistory = useCallback(
    async (
      entries: readonly (HistoryEntryWithState<TExtra> | HistorySnapshot<TExtra>)[],
      index: number,
    ) => {
      engine.restore(entries, index);
      const current =
        engine.getEntriesWithState()[Math.min(Math.max(index, 0), entries.length - 1)];
      if (current) await applyEntry(current.state);
    },
    [engine, applyEntry],
  );

  /** Backwards-compatible legacy shape — `{ states, index }` with
   *  `states` as the plain snapshot list. Used by the IDB session-
   *  store, which still serializes the snapshot-only payload. */
  const getHistory = useCallback(() => {
    const list = engine.getEntriesWithState();
    return {
      states: list.map((e) => e.state),
      index: list.length > 0 ? list.length - 1 : -1,
    };
  }, [engine]);

  /** Rich entry list INCLUDING per-entry state, label, commandId,
   *  isManual, timestamp, and id. The persistence layer writes this so
   *  the History panel reads its original labels after a reload —
   *  `getHistory`'s state-only shape loses metadata and the engine has
   *  to synthesize "Step N" fallbacks on restore. */
  const getFullHistory = useCallback(() => {
    const entries = engine.getEntriesWithState();
    return {
      entries: entries.slice(),
      index: entries.length > 0 ? entries.length - 1 : -1,
    };
  }, [engine]);

  /** Rich entry list (metadata only) for the History panel UI.
   *  Returns a fresh array on every call but only changes identity
   *  when the engine mutates (subscribers wired via
   *  useSyncExternalStore re-render). */
  const getEntries = useCallback((): readonly HistoryEntry[] => engine.getEntries(), [engine]);

  /** Id of the entry currently applied to the canvas. Drives the
   *  panel's "current row" highlight. */
  const getCurrentEntryId = useCallback(() => engine.getCurrentEntryId(), [engine]);

  return {
    setCanvas,
    setExtraStateAccess,
    setPendingLabel,
    setDefaultLabelResolver,
    saveSnapshot,
    manualSnapshot,
    undoCanvas,
    redoCanvas,
    jumpTo,
    clearHistory: () => engine.clear(),
    getHistory,
    getFullHistory,
    getEntries,
    getCurrentEntryId,
    restoreHistory,
    canUndo: engine.canUndo,
    canRedo: engine.canRedo,
    undoCount: engine.undoCount,
    redoCount: engine.redoCount,
    isRestoring: isRestoringRef,
  };
}
